import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import ContactDetail from '../components/ContactDetail';
import ContactDisambiguationModal from '../components/ContactDisambiguationModal';
import ContactCreateConfirmationModal from '../components/ContactCreateConfirmationModal';

function MyPipeline() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  // Disambiguation state (when multiple contacts match)
  const [disambigOpen, setDisambigOpen] = useState(false);
  const [disambigMatches, setDisambigMatches] = useState([]);
  const [pendingVoiceIntent, setPendingVoiceIntent] = useState(null);

  // Create-contact confirmation state
  const [createConfirmOpen, setCreateConfirmOpen] = useState(false);
  const [proposedContact, setProposedContact] = useState(null);

  // View lead modal state
  const [viewingId, setViewingId] = useState(null);
  const [viewingLead, setViewingLead] = useState(null);
  const [viewError, setViewError] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const closeView = () => {
    setViewingId(null);
    setViewingLead(null);
    setViewError(null);
  };

  const handleView = async (id) => {
    try {
      setViewingId(id);
      setViewingLead(null);
      setViewError(null);
      setViewLoading(true);

      // Use the existing API service; it will include auth headers
      const data = await api.getContact(id);
      setViewingLead(data);
    } catch (e) {
      setViewError(e?.message || String(e));
    } finally {
      setViewLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadLeads = async () => {
    setLoading(true);
    try {
      const status = filter === 'all' ? null : filter;
      const data = await api.getMyPipeline(status);
      setLeads(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Failed to load pipeline:', error);
      setLeads([]);
    } finally {
      setLoading(false);
    }
  };

  const tryParseSelectionIndex = (text) => {
    const s = String(text || '').trim().toLowerCase();
    if (!s) return null;

    // "number 2" / "option 2" / "pick 2" / "2"
    const m = s.match(/(?:number|option|pick|choose)\\s*(\\d{1,2})/i) || s.match(/^(\\d{1,2})$/);
    if (!m) return null;
    const n = parseInt(m[1], 10);
    return Number.isFinite(n) ? n : null;
  };

  const isConfirmPhrase = (text) => {
    const s = String(text || '').trim().toLowerCase();
    return !!s && /^(confirm|yes|yeah|yep|okay|ok|do it|create it|save it)$/.test(s);
  };

  const isCancelPhrase = (text) => {
    const s = String(text || '').trim().toLowerCase();
    return !!s && /^(cancel|no|nope|stop|never mind|nevermind)$/.test(s);
  };

  const confirmProposedContact = async () => {
    if (!proposedContact) return;

    try {
      const res = await api.confirmCreateContact(proposedContact);

      if (res?.success) {
        await loadLeads();
      }
    } finally {
      setCreateConfirmOpen(false);
      setProposedContact(null);
    }
  };

  const cancelProposedContact = () => {
    setCreateConfirmOpen(false);
    setProposedContact(null);
  };

  const resolveDisambiguation = async (match) => {
    if (!match?.contact_id || !pendingVoiceIntent) return;

    setDisambigOpen(false);

    try {
      const execResult = await api.executeVoiceIntent({
        intent: pendingVoiceIntent,
        contactId: match.contact_id,
      });

      if (execResult?.success) {
        await loadLeads();
      }
    } finally {
      setPendingVoiceIntent(null);
      setDisambigMatches([]);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      new: '#64748b',
      contacted: 'var(--brand-primary, #3b82f6)',
      qualified: '#8b5cf6',
      proposal: '#f59e0b',
      closed_won: '#22c55e',
      closed_lost: '#ef4444',
    };
    return colors[status] || '#64748b';
  };

  const getStatusLabel = (status) => {
    const labels = {
      new: 'New',
      contacted: 'Contacted',
      qualified: 'Qualified',
      proposal: 'Proposal',
      closed_won: 'Closed Won',
      closed_lost: 'Closed Lost',
    };
    return labels[status] || status;
  };

  const getSourceBadge = (source) => {
    const colors = {
      chat: '#8b5cf6',
      manual: '#64748b',
      import: '#f59e0b',
      dms: 'var(--brand-primary, #3b82f6)',
    };
    return (
      <span
        style={{
          ...styles.sourceBadge,
          backgroundColor: colors[source] || '#64748b',
        }}
      >
        {source}
      </span>
    );
  };

  const filters = [
    { id: 'all', label: 'All Leads' },
    { id: 'new', label: 'New' },
    { id: 'contacted', label: 'Contacted' },
    { id: 'qualified', label: 'Qualified' },
    { id: 'proposal', label: 'Proposal' },
    { id: 'closed_won', label: 'Won' },
    { id: 'closed_lost', label: 'Lost' },
  ];

  if (loading) {
    return <div style={styles.loading}>Loading your pipeline...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>My Pipeline</h1>
          <p style={styles.subtitle}>You have {leads.length} leads in your pipeline</p>
        </div>
        <button onClick={loadLeads} style={styles.refreshBtn}>Refresh</button>
      </div>

      {/* Filters */}
      <div style={styles.filters}>
        {filters.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            style={{
              ...styles.filterButton,
              ...(filter === f.id ? styles.filterButtonActive : {}),
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      <ContactDisambiguationModal
        open={disambigOpen}
        matches={disambigMatches}
        title="Multiple contacts matched"
        subtitle='Tap an option, or say "number 2".'
        onSelect={(m) => resolveDisambiguation(m)}
        onClose={() => {
          setDisambigOpen(false);
          setPendingVoiceIntent(null);
          setDisambigMatches([]);
        }}
      />

      <ContactCreateConfirmationModal
        open={createConfirmOpen}
        proposed={proposedContact}
        onConfirm={confirmProposedContact}
        onCancel={cancelProposedContact}
      />

      {/* Leads List */}
      {leads.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📭</div>
          <div style={styles.emptyText}>No leads in your pipeline</div>
          <a href="/available-leads" style={styles.emptyLink}>
            Check available leads →
          </a>
        </div>
      ) : (
        <div style={styles.leadsList}>
          {leads.map((lead) => (
            <div key={lead.id} style={styles.leadCard}>
              <div style={styles.leadHeader}>
                <div>
                  <div style={styles.leadName}>
                    {lead.first_name} {lead.last_name}
                  </div>
                  {lead.company_name && <div style={styles.companyName}>{lead.company_name}</div>}
                </div>
                <div style={styles.leadBadges}>
                  {getSourceBadge(lead.source)}
                  <span
                    style={{
                      ...styles.statusBadge,
                      backgroundColor: getStatusColor(lead.status),
                    }}
                  >
                    {getStatusLabel(lead.status)}
                  </span>
                </div>
              </div>

              <div style={styles.leadDetails}>
                {lead.email && <div style={styles.detail}>📧 {lead.email}</div>}
                {lead.phone && <div style={styles.detail}>📞 {lead.phone}</div>}
              </div>

              {/* Lead Snapshot (auto-extracted) */}
              {lead.lead_snapshot && (
                <div style={styles.snapshotRow}>
                  {typeof lead.lead_snapshot.intent_score === 'number' && (
                    <span style={styles.chip}>Intent {lead.lead_snapshot.intent_score}</span>
                  )}
                  {lead.lead_snapshot.purchase_timeline && (
                    <span style={styles.chip}>{lead.lead_snapshot.purchase_timeline}</span>
                  )}
                  {(lead.lead_snapshot.budget_min || lead.lead_snapshot.budget_max) && (
                    <span style={styles.chip}>
                      {lead.lead_snapshot.budget_min || ''}
                      {lead.lead_snapshot.budget_min && lead.lead_snapshot.budget_max ? '–' : ''}
                      {lead.lead_snapshot.budget_max || ''}
                    </span>
                  )}
                  {lead.lead_snapshot.vehicle_type && <span style={styles.chip}>{lead.lead_snapshot.vehicle_type}</span>}
                  {typeof lead.lead_snapshot.has_trade_in === 'boolean' && (
                    <span style={styles.chip}>Trade: {lead.lead_snapshot.has_trade_in ? 'Y' : 'N'}</span>
                  )}
                  {lead.lead_snapshot.finance_preference && (
                    <span style={styles.chip}>{lead.lead_snapshot.finance_preference}</span>
                  )}
                  {lead.lead_snapshot.next_action && <span style={styles.chip}>{lead.lead_snapshot.next_action}</span>}
                </div>
              )}

              <div style={styles.leadFooter}>
                <div style={styles.claimedDate}>Claimed {new Date(lead.claimed_at).toLocaleDateString('en-AU')}</div>
                <button style={styles.viewButton} onClick={() => handleView(lead.id)}>
                  View →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Lead Details */}
      {viewingId && !viewingLead && (
        <div style={styles.modalOverlay} onClick={closeView}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Lead Details</div>
              <button style={styles.modalClose} onClick={closeView}>
                ✕
              </button>
            </div>

            {viewLoading && <div style={{ color: '#94a3b8' }}>Loading…</div>}
            {viewError && <div style={{ color: '#ef4444' }}>{viewError}</div>}
          </div>
        </div>
      )}

      {viewingLead && (
        <ContactDetail
          contact={viewingLead}
          onClose={closeView}
          onActivityAdded={() => {}}
          onDeleted={(deletedId) => {
            setLeads((prev) => (Array.isArray(prev) ? prev.filter((l) => l.id !== deletedId) : prev));
            closeView();
          }}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '800px',
    padding: '20px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '10px',
  },
  title: {
    color: '#f1f5f9',
    margin: '0 0 8px 0',
    fontSize: '24px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#94a3b8',
    margin: '0 0 6px 0',
    fontSize: '14px',
  },
  refreshBtn: {
    padding: '8px 16px',
    fontSize: '13px',
    background: 'var(--brand-primary, #3b82f6)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    minHeight: '44px',
  },
  filters: {
    marginTop: '12px',
    marginBottom: '18px',
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  filterButton: {
    padding: '8px 16px',
    fontSize: '14px',
    background: '#1e293b',
    color: 'var(--brand-secondary, #94a3b8)',
    border: '1px solid var(--brand-secondary, #334155)',
    borderRadius: '20px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  filterButtonActive: {
    background: 'var(--brand-primary, #3b82f6)',
    color: 'white',
    borderColor: 'var(--brand-primary, #3b82f6)',
  },
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#94a3b8',
  },
  emptyIcon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  emptyText: {
    marginBottom: '12px',
  },
  emptyLink: {
    color: 'var(--brand-primary, #3b82f6)',
    textDecoration: 'none',
  },
  leadsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  leadCard: {
    background: '#0f172a',
    border: '1px solid #1e293b',
    borderRadius: '12px',
    padding: '16px',
    cursor: 'pointer',
    transition: 'border-color 0.2s',
  },
  leadHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '12px',
  },
  leadName: {
    color: '#f1f5f9',
    fontSize: '16px',
    fontWeight: '600',
  },
  companyName: {
    color: '#94a3b8',
    fontSize: '13px',
    marginTop: '4px',
  },
  leadBadges: {
    display: 'flex',
    gap: '8px',
  },
  sourceBadge: {
    padding: '4px 8px',
    fontSize: '12px',
    color: 'white',
    borderRadius: '4px',
  },
  statusBadge: {
    padding: '4px 8px',
    fontSize: '12px',
    color: 'white',
    borderRadius: '4px',
  },
  leadDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    marginBottom: '12px',
    fontSize: '13px',
  },
  detail: {
    color: '#cbd5e1',
  },
  snapshotRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '12px',
  },
  chip: {
    background: 'rgba(59, 130, 246, 0.2)',
    color: '#93c5fd',
    padding: '4px 8px',
    borderRadius: '4px',
    fontSize: '12px',
  },
  leadFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '13px',
  },
  claimedDate: {
    color: '#64748b',
  },
  viewButton: {
    padding: '6px 12px',
    fontSize: '13px',
    background: 'var(--brand-primary, #3b82f6)',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 999,
  },
  modal: {
    background: '#0f172a',
    border: '1px solid #1e293b',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '500px',
    width: '90%',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  modalTitle: {
    color: '#f1f5f9',
    fontSize: '18px',
    fontWeight: '600',
  },
  modalClose: {
    background: 'none',
    border: 'none',
    color: '#94a3b8',
    fontSize: '20px',
    cursor: 'pointer',
  },
  loading: {
    color: '#94a3b8',
    padding: '20px',
  },
};

export default MyPipeline;
